import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Camera,
  X,
  AlertCircle,
  RefreshCw,
  Upload,
  Plus,
  Minus,
  Zap,
  ZapOff,
} from "lucide-react";

export default function SafetyCameraCaptureModal({
  open,
  onClose,
  onCapture,
  title = "Take Photo",
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [cameraError, setCameraError] = useState("");
  const [cameraLoading, setCameraLoading] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");

  // Zoom controls state
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(4);
  const [zoomStep, setZoomStep] = useState(0.2);
  const [isHardwareZoomApplied, setIsHardwareZoomApplied] = useState(false);

  // Torch / Flashlight state
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // Pinch-to-zoom gesture state
  const [isPinching, setIsPinching] = useState(false);
  const touchStartDistRef = useRef(null);
  const touchStartZoomRef = useRef(1);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setTorchOn(false);
    setHasTorch(false);
    setIsHardwareZoomApplied(false);
    setZoom(1);
  };

  const applyZoom = useCallback(
    async (targetZoom, skipHardware = false) => {
      const clamped = Math.min(Math.max(targetZoom, minZoom), maxZoom);
      const rounded = Math.round(clamped * 10) / 10;
      setZoom(rounded);

      if (skipHardware) {
        setIsHardwareZoomApplied(false);
        return;
      }

      const track = streamRef.current?.getVideoTracks()?.[0];
      const caps = track?.getCapabilities ? track.getCapabilities() : null;

      if (caps && "zoom" in caps && track?.applyConstraints) {
        try {
          await track.applyConstraints({ advanced: [{ zoom: rounded }] });
          const currentSettings = track.getSettings?.();
          if (
            currentSettings?.zoom !== undefined &&
            Math.abs(currentSettings.zoom - rounded) < 0.2
          ) {
            setIsHardwareZoomApplied(true);
            return;
          }
        } catch (err) {
          console.warn("Hardware zoom error, using software zoom fallback:", err);
        }
      }

      setIsHardwareZoomApplied(false);
    },
    [minZoom, maxZoom]
  );

  const handleZoomIn = () => {
    applyZoom(zoom + zoomStep);
  };

  const handleZoomOut = () => {
    applyZoom(zoom - zoomStep);
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()?.[0];
    if (!track || !hasTorch) return;
    try {
      const nextState = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: nextState }] });
      setTorchOn(nextState);
    } catch (err) {
      console.warn("Toggle torch failed:", err);
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      touchStartDistRef.current = dist;
      touchStartZoomRef.current = zoom;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDistRef.current) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const factor = dist / touchStartDistRef.current;
      const newZoom = touchStartZoomRef.current * factor;
      applyZoom(newZoom, true);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
      touchStartDistRef.current = null;
      applyZoom(zoom, false);
    }
  };

  const handleWheel = (e) => {
    if (e.deltaY < 0) {
      applyZoom(zoom + zoomStep);
    } else if (e.deltaY > 0) {
      applyZoom(zoom - zoomStep);
    }
  };

  const startCamera = async () => {
    setCameraLoading(true);
    setCameraError("");

    try {
      if (
        typeof navigator === "undefined" ||
        (!navigator.mediaDevices?.getUserMedia &&
          !navigator.getUserMedia &&
          !navigator.webkitGetUserMedia &&
          !navigator.mozGetUserMedia)
      ) {
        throw new Error(
          "Camera access is not supported in this browser. Please use the device camera or upload option below."
        );
      }

      let stream = null;

      // 1. Try to enumerate available video devices
      let videoDevices = [];
      if (navigator.mediaDevices?.enumerateDevices) {
        try {
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          videoDevices = allDevices.filter((d) => d.kind === "videoinput");
        } catch (e) {
          console.warn("Could not enumerate devices:", e);
        }
      }

      const isMobile =
        typeof navigator !== "undefined" &&
        /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

      // Strategy 1: If on mobile, try ideal facingMode
      if (isMobile && navigator.mediaDevices?.getUserMedia) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: facingMode },
            },
          });
        } catch (e) {
          console.warn("Mobile facingMode failed, falling back:", e);
        }
      }

      // Strategy 2: If we have enumerated video devices, try deviceId
      if (!stream && videoDevices.length > 0 && navigator.mediaDevices?.getUserMedia) {
        for (const dev of videoDevices) {
          if (dev.deviceId) {
            try {
              stream = await navigator.mediaDevices.getUserMedia({
                video: {
                  deviceId: { ideal: dev.deviceId },
                },
              });
              if (stream) break;
            } catch (e) {
              console.warn("DeviceId attempt failed:", e);
            }
          }
        }
      }

      // Strategy 3: Standard generic video: true constraint (safest for desktop/laptop webcams)
      if (!stream && navigator.mediaDevices?.getUserMedia) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
        } catch (e) {
          console.warn("Generic video: true failed:", e);
          throw e;
        }
      }

      // Strategy 4: Legacy vendor-prefixed getUserMedia
      if (!stream) {
        const legacyGetUserMedia =
          navigator.getUserMedia ||
          navigator.webkitGetUserMedia ||
          navigator.mozGetUserMedia;

        if (legacyGetUserMedia) {
          stream = await new Promise((resolve, reject) => {
            legacyGetUserMedia.call(navigator, { video: true }, resolve, reject);
          });
        }
      }

      if (!stream) {
        throw new Error("Could not initialize video stream from your camera.");
      }

      streamRef.current = stream;

      // Detect hardware zoom and torch capabilities
      const track = stream.getVideoTracks()?.[0];
      const caps = track?.getCapabilities ? track.getCapabilities() : null;

      if (caps && "zoom" in caps) {
        const capMin = caps.zoom.min || 1;
        const capMax = Math.min(caps.zoom.max || 4, 5);
        setMinZoom(capMin);
        setMaxZoom(capMax);
        setZoomStep(caps.zoom.step && caps.zoom.step >= 0.1 ? caps.zoom.step : 0.2);
      } else {
        setMinZoom(1);
        setMaxZoom(4);
        setZoomStep(0.2);
      }

      if (caps && "torch" in caps) {
        setHasTorch(true);
      } else {
        setHasTorch(false);
        setTorchOn(false);
      }

      setZoom(1);
      setIsHardwareZoomApplied(false);

      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;
        video.playsInline = true;
        video.muted = true;
        video.autoplay = true;
        video.onloadedmetadata = () => {
          video.play().catch((playErr) => {
            console.warn("video.play() onloadedmetadata failed:", playErr);
          });
        };
        try {
          await video.play();
        } catch (playErr) {
          console.warn("video.play() initial attempt:", playErr);
        }
      }
    } catch (error) {
      console.error("Unable to access camera", error);

      if (
        error?.name === "NotAllowedError" ||
        error?.name === "PermissionDeniedError"
      ) {
        setCameraError(
          "Camera permission was denied. Please allow camera access in your browser or use the button below."
        );
      } else if (
        error?.name === "NotFoundError" ||
        error?.name === "DevicesNotFoundError"
      ) {
        setCameraError(
          "No camera stream could be opened directly. You can use your device camera or pick a photo using the button below."
        );
      } else if (
        error?.name === "NotReadableError" ||
        error?.name === "TrackStartError"
      ) {
        setCameraError(
          "Camera is currently in use by another application. Please close other apps using the webcam or choose a photo below."
        );
      } else {
        setCameraError(
          error?.message ||
            "Unable to access live camera. You can still take or upload a photo below."
        );
      }
    } finally {
      setCameraLoading(false);
    }
  };

  const toggleCamera = () => {
    stopCamera();
    setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
  };

  useEffect(() => {
    if (!open) {
      stopCamera();
      return;
    }

    startCamera();

    return () => {
      stopCamera();
    };
  }, [open, facingMode]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    const videoWidth = video.videoWidth || video.clientWidth || 1280;
    const videoHeight = video.videoHeight || video.clientHeight || 720;

    const canvas = document.createElement("canvas");
    canvas.width = videoWidth;
    canvas.height = videoHeight;

    const context = canvas.getContext("2d");
    if (!context) return;

    if (isHardwareZoomApplied) {
      // Camera sensor delivered pre-zoomed frames
      context.drawImage(video, 0, 0, videoWidth, videoHeight);
    } else {
      // Software crop matching the exact visual center crop seen in the preview
      const currentZoom = Math.max(1, zoom);
      const cropWidth = videoWidth / currentZoom;
      const cropHeight = videoHeight / currentZoom;
      const cropX = (videoWidth - cropWidth) / 2;
      const cropY = (videoHeight - cropHeight) / 2;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(
        video,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        videoWidth,
        videoHeight
      );
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const capturedFile = new File(
          [blob],
          `observation-camera-${timestamp}.jpg`,
          {
            type: "image/jpeg",
            lastModified: Date.now(),
          }
        );

        onCapture?.(capturedFile);
        stopCamera();
        onClose?.();
      },
      "image/jpeg",
      0.92
    );
  };

  const handleClose = () => {
    stopCamera();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onCapture?.(file);
      handleClose();
    }
    e.target.value = "";
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-orange-600">
              <Camera size={18} />
            </div>
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Torch / Flashlight Toggle (if supported) */}
            {!cameraError && hasTorch && (
              <button
                type="button"
                onClick={toggleTorch}
                title={torchOn ? "Turn Torch Off" : "Turn Torch On"}
                aria-label={torchOn ? "Turn Torch Off" : "Turn Torch On"}
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  torchOn
                    ? "bg-amber-400 text-slate-950 shadow-sm"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                {torchOn ? <Zap size={16} /> : <ZapOff size={16} />}
              </button>
            )}

            {/* Switch Camera (Front/Rear) */}
            {!cameraError && (
              <button
                type="button"
                onClick={toggleCamera}
                title="Switch Camera (Front/Rear)"
                aria-label="Switch Camera"
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <RefreshCw size={16} />
              </button>
            )}

            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Video / Preview Area */}
        <div
          className="relative flex min-h-[340px] w-full items-center justify-center bg-black p-2 overflow-hidden select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          {cameraLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 text-white gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              <span className="text-xs font-medium">Starting camera...</span>
            </div>
          )}

          {cameraError ? (
            <div className="mx-2 flex flex-col items-center gap-4 text-center text-white py-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/20 text-red-400">
                <AlertCircle size={32} />
              </div>
              <div className="max-w-sm space-y-1">
                <p className="text-sm font-semibold text-white">Live Camera Preview Unavailable</p>
                <p className="text-xs text-slate-300 leading-relaxed">{cameraError}</p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-lg hover:bg-orange-700 active:scale-95 transition-all"
                >
                  <Camera size={18} />
                  <span>Open Camera / Choose Photo</span>
                </button>

                <button
                  type="button"
                  onClick={startCamera}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
                >
                  <RefreshCw size={14} />
                  <span>Retry Camera</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="relative w-full h-full min-h-[320px] max-h-[60vh] flex items-center justify-center overflow-hidden rounded-xl bg-black">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                onLoadedMetadata={(e) => {
                  e.target.play().catch((err) => console.warn("play on loadedmetadata failed:", err));
                }}
                style={{
                  transform: isHardwareZoomApplied ? "none" : `scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: isPinching ? "none" : "transform 0.15s ease-out",
                }}
                className="w-full h-full min-h-[320px] max-h-[60vh] rounded-xl object-contain bg-black"
              />

              {/* Floating Zoom In & Out (+ and -) Controls Overlay */}
              {!cameraLoading && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1.5 backdrop-blur-md border border-white/20 shadow-2xl text-white select-none">
                  {/* Zoom Out Button (-) */}
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    disabled={zoom <= minZoom + 0.05}
                    title="Zoom Out (-)"
                    aria-label="Zoom Out"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <Minus size={16} strokeWidth={2.5} />
                  </button>

                  {/* Preset Buttons (1x, 2x, 3x) */}
                  <div className="flex items-center gap-1">
                    {[1, 2, 3].map((preset) => {
                      const isActive = Math.abs(zoom - preset) < 0.1;
                      return (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => applyZoom(preset)}
                          title={`Set zoom to ${preset}x`}
                          aria-label={`Set zoom to ${preset}x`}
                          className={`h-7 px-2.5 rounded-full text-xs font-bold transition-all ${
                            isActive
                              ? "bg-orange-500 text-white shadow ring-1 ring-white/60 scale-105"
                              : "bg-white/10 text-slate-200 hover:bg-white/20 hover:text-white"
                          }`}
                        >
                          {preset}x
                        </button>
                      );
                    })}
                  </div>

                  {/* Custom Zoom Indicator Badge if between presets */}
                  {![1, 2, 3].some((p) => Math.abs(zoom - p) < 0.1) && (
                    <button
                      type="button"
                      onClick={() => applyZoom(1)}
                      title="Click to reset zoom to 1x"
                      aria-label={`Current zoom ${zoom.toFixed(1)}x, click to reset`}
                      className="h-7 px-2.5 rounded-full bg-orange-600 text-white text-xs font-bold shadow ring-1 ring-white/60 scale-105 transition-all"
                    >
                      {zoom.toFixed(1)}x
                    </button>
                  )}

                  {/* Zoom In Button (+) */}
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    disabled={zoom >= maxZoom - 0.05}
                    title="Zoom In (+)"
                    aria-label="Zoom In"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <Plus size={16} strokeWidth={2.5} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hidden File Input for Native Camera / File fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Footer Actions */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>

          {cameraError ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-orange-700 transition-colors shadow-sm"
            >
              <Upload size={15} />
              Open Camera / Choose Photo
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCapture}
              disabled={cameraLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm"
            >
              <Camera size={16} />
              Capture Photo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
